import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  ListVideo,
  LockKeyhole,
  MessageSquare,
  Pause,
  Play,
  X,
} from 'lucide-react';
import {
  Builder,
  Eyebrow,
  MarkerUnderline,
  RaceBib,
  Tom,
} from '@/components/foundry';
import { useConvexQuery } from '../lib/useConvex';
import { useOSChrome } from '../components/OSV2Shell';
import InstallAcquisitionOsCard, {
  AIOS_INSTALL_COMPLETED_KEY,
} from '../components/InstallAcquisitionOsCard';
import { useAppAuth } from '@/lib/appAuth';

// Build Room is the guided workshop layer. Academy is the video-module content
// inside this path, not a separate Builder destination.

type BuildRoomStatus =
  | 'intake'
  | 'build_brief'
  | 'building'
  | 'needs_decision'
  | 'qa_review'
  | 'ready'
  | 'handed_off'
  | 'blocked';

type WorkshopState = 'done' | 'active' | 'review' | 'queued';
type ModuleState = 'done' | 'active' | 'queued' | 'locked';
type BuildRoomView = 'library' | 'player';

interface BuildRoomSnapshot {
  status: BuildRoomStatus;
  activeSpecialist: string | null;
}

interface VideoModule {
  code: string;
  title: string;
  duration: string;
  summary: string;
  videoSrc?: string;
  posterSrc?: string;
  transcriptHref?: string;
}

interface Workshop {
  num: '00' | '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08';
  name: string;
  label: string;
  description: string;
  builderMove: string;
  modules: VideoModule[];
}

interface BuildRoomProgress {
  completedModuleIds: string[];
  currentModuleId: string | null;
}

const STATUS_LABELS: Record<BuildRoomStatus, string> = {
  intake: 'Getting ready',
  build_brief: 'Module 00 ready',
  building: 'Build running',
  needs_decision: 'Answer needed',
  qa_review: 'In review',
  ready: 'Ready for handoff',
  handed_off: 'Handed off',
  blocked: 'Paused',
};

const WORKSHOPS: Workshop[] = [
  {
    num: '00',
    name: 'The Shift',
    label: 'Start here',
    description: 'Understand the market shift and why the system has to get smarter.',
    builderMove: 'Name the system you are building first and the reason it matters commercially.',
    modules: [
      {
        code: '0.1',
        title: 'The Landscape Has Changed',
        duration: '8:42',
        summary: 'Why acquisition now has to account for AI-assisted buyers.',
      },
      {
        code: '0.2',
        title: 'Benefit Or Break',
        duration: '9:18',
        summary: 'Use the simple test that shows whether stronger AI helps or hurts.',
      },
      {
        code: '0.3',
        title: 'What We Are Building',
        duration: '7:56',
        summary: 'See the OS as a way to talk to the business, not a dashboard dump.',
      },
      {
        code: '0.4',
        title: 'Open Your Operating Workspace',
        duration: '6:14',
        summary: 'Find the start file, current status, and one next move.',
      },
      {
        code: '0.5',
        title: 'How Your AI Co-Founder Works',
        duration: '6:48',
        summary: 'Your prompts teach as they build. Not a generic prompt library.',
      },
      {
        code: '0.6',
        title: 'The Working Rhythm',
        duration: '5:32',
        summary: 'Principle, action, artifact, proof, next move. The simple loop everything follows.',
      },
    ],
  },
  {
    num: '01',
    name: 'Your AI Acquisition Operator',
    label: 'Connect the system',
    description: 'Set the working start line before route or campaign decisions.',
    builderMove: 'Confirm what is connected, what is missing, and what needs Tom to review.',
    modules: [
      {
        code: '1.1',
        title: 'Set The Template Start Line',
        duration: '6:28',
        summary: 'Confirm the current status, latest artifact, blocker, and next move.',
      },
      {
        code: '1.2',
        title: 'What You Own',
        duration: '7:10',
        summary: 'Clarify what you own and what AI Brand Lab provides.',
      },
      {
        code: '1.3',
        title: 'Check Accounts Without Secrets',
        duration: '6:54',
        summary: 'Record safe setup facts without pasting private values.',
      },
      {
        code: '1.4',
        title: 'Tracking Before Traffic',
        duration: '7:22',
        summary: 'Make sure the first test can produce readable signal.',
      },
      {
        code: '1.5',
        title: 'Connect The Acquisition System',
        duration: '8:15',
        summary: 'See what is connected, missing, stale, or blocked.',
      },
      {
        code: '1.6',
        title: 'Lock The Setup',
        duration: '5:44',
        summary: 'Confirm what is ready and what carries into the route decision.',
      },
    ],
  },
  {
    num: '02',
    name: 'Route Before Asset',
    label: 'Choose the route',
    description: 'Accept the buying path before pages, emails, ads, or follow-up are built.',
    builderMove: 'Accept the route and build brief before any assets are made.',
    modules: [
      {
        code: '2.1',
        title: 'Business Consultation',
        duration: '8:38',
        summary: 'Carry your Game Plan numbers and the strategy inputs into route intelligence.',
      },
      {
        code: '2.2',
        title: 'Route Before Asset',
        duration: '7:44',
        summary: 'Why the route decision steers every asset that follows.',
      },
      {
        code: '2.3',
        title: 'Attention Channels Are Not The Funnel',
        duration: '8:20',
        summary: 'Separate social, Meta, outbound, and community signal from the buying path.',
      },
      {
        code: '2.4',
        title: 'Read The Route Rationale',
        duration: '10:02',
        summary: 'See why the chosen route fits the offer, proof, trust, and attention.',
      },
      {
        code: '2.5',
        title: 'Accept The Build Brief',
        duration: '6:48',
        summary: 'Accept or review-hold the first build scope.',
      },
    ],
  },
  {
    num: '03',
    name: 'Acquisition Radar',
    label: 'Find the signal',
    description: 'Turn market evidence into buyer language, proof gaps, and usable insight.',
    builderMove: 'Approve the evidence base before creative work starts.',
    modules: [
      {
        code: '3.1',
        title: 'Research Without Copying',
        duration: '7:18',
        summary: 'Use public evidence without imitating the market.',
      },
      {
        code: '3.2',
        title: 'Turn Public Signals Into Maps',
        duration: '9:46',
        summary: 'Read buyer language, objections, proof patterns, and attention clues.',
      },
      {
        code: '3.3',
        title: 'Approve The Evidence Base',
        duration: '6:52',
        summary: 'Approve, narrow, or review-hold the research before creative work.',
      },
    ],
  },
  {
    num: '04',
    name: 'Creative Intelligence',
    label: 'Shape the assets',
    description: 'Turn the accepted route and proof into testable creative ideas.',
    builderMove: 'Choose the first creative ideas worth testing.',
    modules: [
      {
        code: '4.1',
        title: 'Chosen Funnel Build-Out',
        duration: '8:32',
        summary: 'Turn the accepted route into the first build-out inputs.',
      },
      {
        code: '4.2',
        title: 'Evidence Into Dangles',
        duration: '8:04',
        summary: 'Create testable creative thoughts from evidence, not random hooks.',
      },
      {
        code: '4.3',
        title: 'Briefs And Image Concepts',
        duration: '9:11',
        summary: 'Review draft creative inputs against route, channel, proof, and claim limits.',
      },
      {
        code: '4.4',
        title: 'Claim Limits And QA',
        duration: '6:58',
        summary: 'Keep creative constrained by truth before anything goes live.',
      },
    ],
  },
  {
    num: '05',
    name: 'Campaign Proposal',
    label: 'Preview the run',
    description: 'Review the proposed campaign path before anything is activated.',
    builderMove: 'Read the proposal and decide what can move forward.',
    modules: [
      {
        code: '5.1',
        title: 'Preview Is Not Activation',
        duration: '6:45',
        summary: 'Separate proposal, approval, execution, and launch.',
      },
      {
        code: '5.2',
        title: 'Read The Approval-Gated Plan',
        duration: '8:12',
        summary: 'Review the plan in plain English before any account changes.',
      },
      {
        code: '5.3',
        title: 'Know What Changes',
        duration: '7:36',
        summary: 'See the expected changes, risks, pause plan, and support triggers.',
      },
    ],
  },
  {
    num: '06',
    name: 'Human-Approved Launch',
    label: 'Approve carefully',
    description: 'Use approval, spend caps, and receipts before live traffic or outreach.',
    builderMove: 'Approve the launch controls before anything goes live.',
    modules: [
      {
        code: '6.1',
        title: 'Approval Is The Control Layer',
        duration: '8:02',
        summary: 'Review spend cap, risks, hard blocks, and confirmation.',
      },
      {
        code: '6.2',
        title: 'Paused Staging Is Not Live Launch',
        duration: '7:48',
        summary: 'Read paused or not-run receipts without mistaking them for launch.',
      },
      {
        code: '6.3',
        title: 'Read The Activation Receipt',
        duration: '7:05',
        summary: 'Summarize what is live, or what is still not live yet.',
      },
      {
        code: '6.4',
        title: 'First Signal Loop Read',
        duration: '8:26',
        summary: 'Read the first signal without treating tiny data as final truth.',
      },
    ],
  },
  {
    num: '07',
    name: 'Signal Loop + Leak Review',
    label: 'Find the leak',
    description: 'Read the system, name one bottleneck, and choose one next test.',
    builderMove: 'Name the current leak and choose one next test.',
    modules: [
      {
        code: '7.1',
        title: 'Dashboard Recommendation',
        duration: '7:24',
        summary: 'Use connected context to get one clear recommendation.',
      },
      {
        code: '7.2',
        title: 'Talk To Your Business',
        duration: '8:06',
        summary: 'Ask what the system knows, cannot trust, and needs next.',
      },
      {
        code: '7.3',
        title: 'Read Signal Without Panic',
        duration: '7:58',
        summary: 'Compare channel, conversion, revenue, follow-up, and experiment signals.',
      },
      {
        code: '7.4',
        title: 'Name The Leak',
        duration: '6:42',
        summary: 'Choose the current constraint and what not to change yet.',
      },
      {
        code: '7.5',
        title: 'Accept One Next Test',
        duration: '7:35',
        summary: 'Accept, revise, or review-hold the next test.',
      },
      {
        code: '7.6',
        title: 'Carry The Learning Forward',
        duration: '5:58',
        summary: 'Save what you learned so the next loop starts smarter.',
      },
    ],
  },
  {
    num: '08',
    name: 'Operating With The Lab',
    label: 'Ask for help',
    description: 'Get support from the current context without re-explaining everything.',
    builderMove: 'Ask from the current context, then return to one next move.',
    modules: [
      {
        code: '8.1',
        title: 'Ask Without Re-Onboarding',
        duration: '6:38',
        summary: 'Prepare a support request from the current artifact.',
      },
      {
        code: '8.2',
        title: 'Redact Before You Share',
        duration: '5:54',
        summary: 'Remove secrets and private data before asking for support.',
      },
      {
        code: '8.3',
        title: 'Know When To Escalate',
        duration: '6:46',
        summary: 'Decide answer now, one clarification, or Ask Tom candidate.',
      },
      {
        code: '8.4',
        title: 'Return To One Next Move',
        duration: '5:42',
        summary: 'Put the support answer back into the active build path.',
      },
    ],
  },
];

const WORKSHOP_COVERS: Record<Workshop['num'], string> = {
  '00': '/character-system/scenes/build-room-module-covers-v2/build-room-module-00-the-shift-v1.png',
  '01': '/character-system/scenes/build-room-module-covers-v2/build-room-module-01-acquisition-operator-v1.png',
  '02': '/character-system/scenes/build-room-module-covers-v2/build-room-module-02-route-before-asset-v1.png',
  '03': '/character-system/scenes/build-room-module-covers-v2/build-room-module-03-acquisition-radar-v1.png',
  '04': '/character-system/scenes/build-room-module-covers-v2/build-room-module-04-creative-intelligence-v1.png',
  '05': '/character-system/scenes/build-room-module-covers-v2/build-room-module-05-campaign-proposal-v1.png',
  '06': '/character-system/scenes/build-room-module-covers-v2/build-room-module-06-human-approved-launch-v1.png',
  '07': '/character-system/scenes/build-room-module-covers-v2/build-room-module-07-signal-loop-v1.png',
  '08': '/character-system/scenes/build-room-module-covers-v2/build-room-module-08-operating-with-the-lab-v1.png',
};

// ─────────────────────────────────────────────────────────────────────────
// Post-it card visual variance — Sprint H (2026-05-08).
//
// Per-workshop tilts so the grid feels hand-pinned, not algorithmic. Mirrors
// PostItModulesPreview Variation A (foundry-preview/pages/PostItModulesPreview.tsx),
// rebalanced to two fixed scribbles + one dynamic active scribble — at most
// three Caveat moments per surface (max-3 scribble rule).
// ─────────────────────────────────────────────────────────────────────────

interface PostItStyle {
  cardTilt: number;
  tapeTilt: number;
  bibTilt: number;
  tapeTone: 'teal' | 'coral' | 'ink';
  textureSeed: number;
  /** Fixed scribble that ships regardless of state. Hidden when this card is the active one (replaced by the dynamic active marker). */
  scribble?: { text: string; tone: 'ink' | 'teal' | 'coral'; rotate: number };
}

const POST_IT_STYLES: Record<Workshop['num'], PostItStyle> = {
  '00': { cardTilt: -1.6, tapeTilt: 1.2, bibTilt: -3.5, tapeTone: 'teal', textureSeed: 3, scribble: { text: '← start here', tone: 'coral', rotate: -4 } },
  '01': { cardTilt: 0.8, tapeTilt: -1.0, bibTilt: 4, tapeTone: 'coral', textureSeed: 4 },
  '02': { cardTilt: 2.4, tapeTilt: 0.4, bibTilt: -5, tapeTone: 'ink', textureSeed: 5 },
  '03': { cardTilt: -2.0, tapeTilt: 1.6, bibTilt: 3.5, tapeTone: 'teal', textureSeed: 6 },
  '04': { cardTilt: 1.4, tapeTilt: -0.8, bibTilt: -4, tapeTone: 'coral', textureSeed: 7 },
  '05': { cardTilt: -0.8, tapeTilt: 2.2, bibTilt: 4.5, tapeTone: 'ink', textureSeed: 8 },
  '06': { cardTilt: 2.0, tapeTilt: -1.4, bibTilt: -3, tapeTone: 'teal', textureSeed: 9 },
  '07': { cardTilt: -1.2, tapeTilt: 0.8, bibTilt: 4, tapeTone: 'coral', textureSeed: 10 },
  '08': { cardTilt: 1.8, tapeTilt: -2.0, bibTilt: -4.5, tapeTone: 'ink', textureSeed: 11, scribble: { text: 'next.', tone: 'ink', rotate: -6 } },
};

const POST_IT_TAPE_COLORS: Record<'teal' | 'coral' | 'ink', { bg: string; shadow: string }> = {
  teal: { bg: 'oklch(0.62 0.10 175 / 0.78)', shadow: 'oklch(0.40 0.06 175 / 0.30)' },
  coral: { bg: 'oklch(0.66 0.18 25 / 0.72)', shadow: 'oklch(0.40 0.10 25 / 0.28)' },
  ink: { bg: 'oklch(0.30 0.04 90 / 0.62)', shadow: 'oklch(0.20 0.02 90 / 0.40)' },
};

const POST_IT_SCRIBBLE_COLORS: Record<'ink' | 'teal' | 'coral', string> = {
  ink: 'oklch(0.30 0.02 95)',
  teal: 'oklch(0.50 0.10 175)',
  coral: 'oklch(0.55 0.18 30)',
};

interface PostItStateStamp {
  label: string;
  color: string;
  tilt: number;
}

const POST_IT_STATE_STAMP: Record<WorkshopState, PostItStateStamp> = {
  done:   { label: 'complete', color: 'oklch(0.45 0.13 145)', tilt: -4 },
  active: { label: 'active',   color: 'oklch(0.55 0.18 30)',  tilt: 3  },
  review: { label: 'review',   color: 'oklch(0.50 0.10 175)', tilt: -2 },
  queued: { label: 'locked',   color: 'oklch(0.34 0.02 95)',  tilt: -3 },
};

const FALLBACK: BuildRoomSnapshot = {
  status: 'build_brief',
  activeSpecialist: 'Tom',
};

const PROGRESS_STORAGE_KEY = 'abl:build-room-module-progress:v2';

export default function BuildRoom() {
  const previewMode = isPreviewMode();
  const auth = useAppAuth();
  const progressStorageKey = scopedStorageKey(PROGRESS_STORAGE_KEY, auth.userId);
  const installStorageKey = scopedStorageKey(AIOS_INSTALL_COMPLETED_KEY, auth.userId);
  const { data, loading, error } = useConvexQuery<BuildRoomSnapshot>(
    'buildRoom:getCurrentForUser',
    {},
    { skip: previewMode },
  );
  const snapshot = previewMode ? FALLBACK : data;
  const effectiveSnapshot = snapshot ?? FALLBACK;
  const hasLiveSession = Boolean(data);
  const activeIndex = currentWorkshopIndex(effectiveSnapshot.status);
  const [progress, setProgress] = useState<BuildRoomProgress>(() => readStoredProgress(progressStorageKey));
  const completedModuleIds = useMemo(
    () => new Set(progress.completedModuleIds),
    [progress.completedModuleIds],
  );
  const [selectedIndex, setSelectedIndex] = useState(activeIndex);
  const [view, setView] = useState<BuildRoomView>('library');
  const [lessonsOpen, setLessonsOpen] = useState(true);
  const selectedWorkshop = WORKSHOPS[selectedIndex] ?? WORKSHOPS[0];
  const selectedState = workshopStateFor(effectiveSnapshot.status, selectedIndex);
  const activeModuleIndex = nextModuleIndexFor(selectedWorkshop, selectedState, completedModuleIds);
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(activeModuleIndex);
  const activeModule = useMemo(
    () => selectedWorkshop.modules[selectedModuleIndex] ?? selectedWorkshop.modules[0],
    [selectedModuleIndex, selectedWorkshop],
  );
  const activeModuleId = moduleId(selectedWorkshop, activeModule);
  const moduleComplete = isModuleComplete(selectedWorkshop, activeModule, selectedState, completedModuleIds);
  const [playingModuleId, setPlayingModuleId] = useState<string | null>(null);

  // Durable setup utility: the install card stays at the top of Build Room
  // permanently. The card owns its auth gate internally, so signed-out
  // visitors still see that the workspace setup exists and get a direct
  // sign-in CTA instead of an invisible feature.
  const showInstallCard = true;
  const isPlaying = playingModuleId === activeModuleId;

  useEffect(() => {
    setProgress(readStoredProgress(progressStorageKey));
  }, [progressStorageKey]);

  // Mark Workspace Activation step 03 ("First Build Room visit") on first
  // mount, so the Command Centre activation strip lights up. Replaced the
  // legacy `abl:business-xray-opened` flag after the X-ray retirement.
  useEffect(() => {
    try {
      window.localStorage.setItem('abl:build-room-opened', '1');
    } catch {
      // localStorage unavailable — strip stays on this step.
    }
  }, []);

  // Flip the OS shell into focus mode when the lesson player view opens, so
  // the global rail steps aside and the lesson sidebar becomes the only left
  // chrome (Skool pattern). Reset on unmount + when the Builder backs out to
  // the workshop library.
  const { setFocusMode } = useOSChrome();
  useEffect(() => {
    setFocusMode(view === 'player');
    return () => setFocusMode(false);
  }, [view, setFocusMode]);

  useEffect(() => {
    setSelectedIndex(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    setSelectedModuleIndex(activeModuleIndex);
  }, [activeModuleIndex, selectedIndex]);

  useEffect(() => {
    if (!progress.currentModuleId) return;
    const location = findModuleLocation(progress.currentModuleId);
    if (!location) return;
    setSelectedIndex(location.workshopIndex);
    setSelectedModuleIndex(location.moduleIndex);
  // Run only once after local progress has loaded, so route changes do not fight Builder selection.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistProgress = (next: BuildRoomProgress) => {
    setProgress(next);
    writeStoredProgress(next, progressStorageKey);
  };

  const selectWorkshop = (index: number) => {
    const workshop = WORKSHOPS[index] ?? WORKSHOPS[0];
    const state = workshopStateFor(effectiveSnapshot.status, index);
    const nextIndex = nextModuleIndexFor(workshop, state, completedModuleIds);
    const nextModule = workshop.modules[nextIndex] ?? workshop.modules[0];
    setSelectedIndex(index);
    setSelectedModuleIndex(nextIndex);
    setPlayingModuleId(null);
    persistProgress({
      ...progress,
      currentModuleId: moduleId(workshop, nextModule),
    });
  };

  const openWorkshop = (index: number) => {
    selectWorkshop(index);
    setView('player');
  };

  const selectModule = (index: number) => {
    const nextModule = selectedWorkshop.modules[index] ?? selectedWorkshop.modules[0];
    setSelectedModuleIndex(index);
    setPlayingModuleId(null);
    persistProgress({
      ...progress,
      currentModuleId: moduleId(selectedWorkshop, nextModule),
    });
  };

  const markWatched = () => {
    if (selectedState === 'queued') return;
    const nextCompleted = new Set(progress.completedModuleIds);
    nextCompleted.add(activeModuleId);
    const nextModuleIndex = findNextOpenModuleIndex(selectedWorkshop, selectedModuleIndex, nextCompleted);
    const nextModule = selectedWorkshop.modules[nextModuleIndex] ?? activeModule;
    setPlayingModuleId(null);
    setSelectedModuleIndex(nextModuleIndex);
    persistProgress({
      completedModuleIds: Array.from(nextCompleted),
      currentModuleId: moduleId(selectedWorkshop, nextModule),
    });
  };

  const handleTopBack = () => {
    if (view === 'player') {
      setView('library');
      setPlayingModuleId(null);
      return;
    }

    window.location.href = '?osRoute=lab-home';
  };

  return (
    <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-6 lg:px-7">
      {view === 'library' ? (
        <>
          <TopBar
            snapshot={effectiveSnapshot}
            loading={loading}
            hasLiveSession={hasLiveSession}
            view={view}
            onBack={handleTopBack}
          />
          {!previewMode && error ? <LoadWarning message="Build Room state could not load. Refresh and sign back in before continuing." /> : null}
        </>
      ) : null}

      <main className={cls(view === 'library' && 'mt-5')}>
        {view === 'library' && showInstallCard ? (
          <InstallAcquisitionOsCard
            key={installStorageKey}
            storageKey={installStorageKey}
            onStartModuleOne={() => openWorkshop(0)}
          />
        ) : null}
        {view === 'library' ? (
          <WorkshopLibrary
            activeIndex={activeIndex}
            completedModuleIds={completedModuleIds}
            snapshot={effectiveSnapshot}
            onOpenWorkshop={openWorkshop}
            onStartHere={() => openWorkshop(activeIndex)}
          />
        ) : (
          <WorkshopCard
            activeModule={activeModule}
            activeModuleIndex={activeModuleIndex}
            completedModuleIds={completedModuleIds}
            isPlaying={isPlaying}
            lessonsOpen={lessonsOpen}
            moduleComplete={moduleComplete}
            onBackToLibrary={() => setView('library')}
            onMarkWatched={markWatched}
            onSelectModule={selectModule}
            onSelectWorkshop={selectWorkshop}
            onToggleLessons={() => setLessonsOpen((v) => !v)}
            onTogglePlay={() => setPlayingModuleId(isPlaying ? null : activeModuleId)}
            selectedIndex={selectedIndex}
            selectedModuleIndex={selectedModuleIndex}
            selectedState={selectedState}
            snapshot={effectiveSnapshot}
            workshop={selectedWorkshop}
          />
        )}
      </main>
    </div>
  );
}

function TopBar({
  snapshot,
  loading,
  hasLiveSession,
  view,
  onBack,
}: {
  snapshot: BuildRoomSnapshot;
  loading: boolean;
  hasLiveSession: boolean;
  view: BuildRoomView;
  onBack: () => void;
}) {
  return (
    <header>
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-fd-border pb-3">
        <button
          type="button"
          onClick={onBack}
          className="group inline-flex items-center gap-1.5 font-geist-mono text-[11px] font-bold uppercase tracking-[0.06em] text-fd-text-muted transition-colors hover:text-fd-text"
        >
          <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" strokeWidth={2} />
          {view === 'player' ? 'Back' : 'All products'}
        </button>
        <span className="font-geist-mono text-[11px] font-bold uppercase tracking-[0.08em] text-fd-text-muted">
          AI Acquisition OS
        </span>
        <span className="inline-flex h-5 items-center rounded-[3px] border border-fd-border bg-fd-paper px-1.5 font-geist-mono text-[9.5px] font-bold uppercase leading-none tracking-[0.06em] text-fd-text-subtle">
          {loading && !hasLiveSession ? 'Loading' : STATUS_LABELS[snapshot.status]}
        </span>
      </div>

      <div className="mt-4">
        <h1 className="font-caveat text-[44px] font-medium leading-[0.88] tracking-normal text-fd-text sm:text-[56px]">
          AI Acquisition OS
        </h1>
        <span className="mt-2 block h-[5px] w-[220px] max-w-full rounded-full bg-fd-accent" />
      </div>
    </header>
  );
}

function WorkshopLibrary({
  activeIndex,
  completedModuleIds,
  snapshot,
  onOpenWorkshop,
  onStartHere,
}: {
  activeIndex: number;
  completedModuleIds: Set<string>;
  snapshot: BuildRoomSnapshot;
  onOpenWorkshop: (index: number) => void;
  onStartHere: () => void;
}) {
  return (
    <section className="relative">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-fd-border pb-3">
        <div>
          <Eyebrow className="mb-1 block text-fd-accent-deep">9-module path</Eyebrow>
          <h2 className="font-geist text-[22px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
            Start with The Shift.
          </h2>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-4">
          <p className="max-w-[320px] font-caveat text-[20px] leading-[1.08] text-fd-text">
            One module at a time. Real progress.{' '}
            <MarkerUnderline tone="race" strokeWidth={2} duration={700} delay={200}>
              Real freedom.
            </MarkerUnderline>
          </p>
          <button
            type="button"
            onClick={onStartHere}
            className="inline-flex h-10 items-center gap-2 rounded-[6px] border border-fd-accent-deep bg-fd-accent px-4 font-geist-mono text-[10px] font-black uppercase tracking-[0.06em] text-[oklch(0.16_0.005_95)] shadow-[2px_2px_0_0_var(--fd-text)] transition-transform hover:-translate-y-0.5"
          >
            Start here
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.3} />
          </button>
        </div>
      </div>

      <div
        className="relative isolate overflow-hidden rounded-[14px] border border-fd-border bg-fd-bg p-6 sm:p-8 lg:p-10"
        style={{
          // Ruled-paper notebook background — same pattern as the foundry-preview
          // post-it canvas. Soft canary lines at 32px so the post-its look pinned
          // to a workbook page, not floating in space.
          backgroundImage:
            'linear-gradient(to bottom, transparent 0, transparent 31px, oklch(0.86 0.01 95 / 0.55) 31px, oklch(0.86 0.01 95 / 0.55) 32px, transparent 32px), linear-gradient(to right, transparent 0, transparent 31px, oklch(0.86 0.01 95 / 0.40) 31px, oklch(0.86 0.01 95 / 0.40) 32px, transparent 32px)',
          backgroundSize: '32px 32px',
        }}
      >
        {/* Margin pin-prick texture — coral guideline at the left edge */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-6 left-8 hidden w-px bg-fd-coral/30 lg:block"
          style={{ boxShadow: '0 0 0 0.5px oklch(0.62 0.20 25 / 0.18)' }}
        />

        <div className="grid grid-cols-1 gap-y-12 gap-x-6 sm:grid-cols-2 sm:gap-x-8 xl:grid-cols-3">
          {WORKSHOPS.map((workshop, index) => {
            const state = workshopStateFor(snapshot.status, index);
            const active = index === activeIndex || state === 'review';
            const completedCount = completedCountForWorkshop(workshop, state, completedModuleIds);
            const totalMinutes = totalMinutesForWorkshop(workshop);

            return (
              <WorkshopPostItCard
                key={workshop.num}
                workshop={workshop}
                state={state}
                active={active}
                completedCount={completedCount}
                totalMinutes={totalMinutes}
                onOpen={() => onOpenWorkshop(index)}
              />
            );
          })}
        </div>
      </div>

      <div className="pointer-events-none relative mt-5 hidden h-[126px] lg:block" aria-hidden>
        <Builder
          variant="reading-notebook"
          className="absolute bottom-0 left-2 w-[70px] drop-shadow-[0_14px_12px_rgba(22,18,10,0.14)]"
        />
        <Tom
          variant="hands-on-hips"
          className="absolute bottom-0 right-2 w-[62px] drop-shadow-[0_14px_12px_rgba(22,18,10,0.14)]"
        />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// WorkshopPostItCard — Sprint H (2026-05-08).
//
// The 9-module path's per-card chrome reimagined as a workbook post-it.
// Canary paper with grain + washi tape pinned across the top + counter-
// rotated race-bib stamp + state stamp in tilted Caveat. The active card
// settles flat (rotate: 0) and gets a coral margin scribble pointing at
// it; fixed scribbles ship on Workshop 00 ("← start here") and Workshop
// 08 ("next."). Total Caveat moments per surface: ≤3 (max-3 rule).
// ─────────────────────────────────────────────────────────────────────────

function WorkshopPostItCard({
  workshop,
  state,
  active,
  completedCount,
  totalMinutes,
  onOpen,
}: {
  workshop: Workshop;
  state: WorkshopState;
  active: boolean;
  completedCount: number;
  totalMinutes: number;
  onOpen: () => void;
}) {
  const prefersReducedMotion = useReducedMotion();
  const reduced = Boolean(prefersReducedMotion);
  const style = POST_IT_STYLES[workshop.num];
  const stateStamp = POST_IT_STATE_STAMP[state];
  const tapeColor = POST_IT_TAPE_COLORS[style.tapeTone];
  const isLocked = state === 'queued';
  const total = workshop.modules.length;

  // Active cards sit flat — the active marker scribble replaces any fixed
  // scribble for that workshop, so we don't accumulate Caveat moments.
  const restingTilt = active ? 0 : style.cardTilt;
  const fixedScribble = active ? null : style.scribble;

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      aria-label={`${workshop.name} — ${stateStamp.label}, ${completedCount} of ${total} watched`}
      className="group relative mx-auto w-full max-w-[320px] cursor-pointer focus-visible:outline-none"
      style={{
        rotate: restingTilt,
        transformOrigin: 'center center',
      }}
      whileHover={
        reduced
          ? undefined
          : { rotate: 0, y: -6, scale: 1.015, transition: { type: 'spring', stiffness: 220, damping: 22 } }
      }
      whileFocus={
        reduced
          ? undefined
          : { rotate: 0, y: -2, transition: { type: 'spring', stiffness: 220, damping: 22 } }
      }
      whileTap={
        reduced
          ? undefined
          : { scale: 0.985, transition: { type: 'spring', stiffness: 320, damping: 26 } }
      }
    >
      {/* Card body */}
      <div
        className={cls(
          'relative w-full overflow-visible',
          isLocked && 'opacity-75',
        )}
        style={{
          aspectRatio: '5 / 6',
          background:
            'radial-gradient(120% 110% at 30% 0%, oklch(0.96 0.10 92) 0%, oklch(0.92 0.13 92) 60%, oklch(0.88 0.13 92) 100%)',
          boxShadow:
            '0 1px 0 0 oklch(0.85 0.12 92) inset, ' +
            '-1px 0 0 0 oklch(0.84 0.10 90) inset, ' +
            '14px 18px 28px -14px oklch(0.30 0.04 90 / 0.42), ' +
            '4px 6px 10px -6px oklch(0.30 0.04 90 / 0.30)',
          // Visible focus ring outside the paper edge, dark + offset, no glow.
          outline: 'none',
        }}
      >
        {/* Paper grain — SVG turbulence + multiply blend */}
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.16] mix-blend-multiply"
        >
          <filter id={`postit-grain-${workshop.num}`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed={style.textureSeed} />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.18  0 0 0 0 0.14  0 0 0 0 0.10  0 0 0 0.55 0"
            />
          </filter>
          <rect width="100%" height="100%" filter={`url(#postit-grain-${workshop.num})`} />
        </svg>

        {/* Race-bib stamp — top-right, counter-rotated to break the card tilt */}
        <span
          className="absolute right-3 top-3 z-10"
          style={{ transform: `rotate(${style.bibTilt}deg)`, transformOrigin: 'center center' }}
        >
          <RaceBib n={workshop.num} tone="race" size="md" shadow="sm" />
        </span>

        {/* Module cover — central focal point, paper-multiply integration */}
        <div className="relative flex h-[60%] items-center justify-center px-6 pt-12">
          <img
            src={WORKSHOP_COVERS[workshop.num]}
            alt=""
            className="h-full w-auto max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.04]"
            style={{ filter: 'drop-shadow(0 8px 14px oklch(0.30 0.04 90 / 0.22))' }}
            loading="lazy"
          />
          {isLocked ? (
            <span className="absolute right-3 bottom-1 grid h-7 w-7 place-items-center rounded-full border border-fd-border bg-fd-paper/85 text-fd-text-subtle">
              <LockKeyhole className="h-3.5 w-3.5" strokeWidth={2.2} />
            </span>
          ) : null}
        </div>

        {/* Title block — bottom 40% */}
        <div className="absolute inset-x-0 bottom-0 flex h-[40%] flex-col justify-end gap-1.5 px-5 pb-5">
          <h3 className="font-geist text-[16px] font-medium leading-[1.18] tracking-[-0.015em] text-fd-text [text-wrap:balance]">
            {workshop.name}
          </h3>
          <div className="flex items-end justify-between gap-3">
            <span
              className="font-caveat text-[15px] font-medium leading-none text-fd-text-muted"
              style={{ transform: 'rotate(-1.5deg)', display: 'inline-block' }}
            >
              workshop {workshop.num}
            </span>
            <PostItStateStamp stamp={stateStamp} state={state} />
          </div>
          <p className="font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
            {completedCount}/{total} watched · ~{totalMinutes} min
          </p>
        </div>

        {/* Right-edge paper-thickness highlight */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-2 right-0 w-px"
          style={{ background: 'linear-gradient(to bottom, transparent, oklch(0.78 0.10 90 / 0.38) 18%, oklch(0.78 0.10 90 / 0.38) 82%, transparent)' }}
        />
      </div>

      {/* Washi tape — pinned across top, independent tilt */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 z-20 h-6 w-[110px] sm:w-[124px]"
        style={{
          transform: `translate(-50%, -55%) rotate(${style.tapeTilt}deg)`,
          background: tapeColor.bg,
          maskImage:
            'linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%), linear-gradient(to bottom, transparent 0, black 8%, black 92%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%), linear-gradient(to bottom, transparent 0, black 8%, black 92%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          boxShadow:
            '0 1px 0 0 oklch(1 0 0 / 0.18) inset, ' +
            `0 5px 8px -4px ${tapeColor.shadow}`,
        }}
      >
        <span
          className="absolute inset-0 opacity-[0.32] mix-blend-multiply"
          style={{
            backgroundImage:
              'repeating-linear-gradient(115deg, oklch(0.40 0.04 95 / 0.12) 0 2px, transparent 2px 6px)',
          }}
        />
      </span>

      {/* Active marker scribble — coral "← here" pointing at the active card */}
      {active ? (
        <span
          aria-hidden
          className="pointer-events-none absolute z-10 font-caveat text-[15px] font-medium whitespace-nowrap"
          style={{
            bottom: '14px',
            right: '-46px',
            color: POST_IT_SCRIBBLE_COLORS.coral,
            transform: 'rotate(-4deg)',
            transformOrigin: 'left center',
          }}
        >
          ← here
        </span>
      ) : fixedScribble ? (
        <span
          aria-hidden
          className="pointer-events-none absolute z-10 font-caveat text-[15px] font-medium whitespace-nowrap"
          style={{
            bottom: '8px',
            right: '-44px',
            color: POST_IT_SCRIBBLE_COLORS[fixedScribble.tone],
            transform: `rotate(${fixedScribble.rotate}deg)`,
            transformOrigin: 'left center',
          }}
        >
          {fixedScribble.text}
        </span>
      ) : null}

      {/* Focus-visible ring — sits outside the paper, square corners to match the post-it edge */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[2px] outline-none ring-0 transition-shadow group-focus-visible:ring-2 group-focus-visible:ring-fd-text group-focus-visible:ring-offset-4"
      />
    </motion.button>
  );
}

// State stamp — tilted Caveat with state-coloured ink, optional lock icon
function PostItStateStamp({
  stamp,
  state,
}: {
  stamp: PostItStateStamp;
  state: WorkshopState;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 font-caveat text-[18px] font-medium leading-none"
      style={{
        transform: `rotate(${stamp.tilt}deg)`,
        color: stamp.color,
      }}
    >
      {state === 'queued' && <LockKeyhole className="h-3 w-3" strokeWidth={2.4} />}
      {state === 'done' && <Check className="h-3 w-3" strokeWidth={2.6} />}
      {stamp.label}
    </span>
  );
}

function WorkshopCard({
  activeModule,
  activeModuleIndex,
  completedModuleIds,
  isPlaying,
  lessonsOpen,
  moduleComplete,
  onBackToLibrary,
  onMarkWatched,
  onSelectModule,
  onSelectWorkshop,
  onToggleLessons,
  onTogglePlay,
  selectedIndex,
  selectedModuleIndex,
  selectedState,
  snapshot,
  workshop,
}: {
  activeModule: VideoModule;
  activeModuleIndex: number;
  completedModuleIds: Set<string>;
  isPlaying: boolean;
  lessonsOpen: boolean;
  moduleComplete: boolean;
  onBackToLibrary: () => void;
  onMarkWatched: () => void;
  onSelectModule: (index: number) => void;
  onSelectWorkshop: (index: number) => void;
  onToggleLessons: () => void;
  onTogglePlay: () => void;
  selectedIndex: number;
  selectedModuleIndex: number;
  selectedState: WorkshopState;
  snapshot: BuildRoomSnapshot;
  workshop: Workshop;
}) {
  const locked = selectedState === 'queued';

  return (
    <section className="min-w-0">
      {/* Circle-style header bar: back arrow + course title left, lessons toggle right */}
      <header className="mb-5 flex items-center justify-between gap-4 border-b border-fd-border pb-4">
        <button
          type="button"
          onClick={onBackToLibrary}
          className="group inline-flex items-center gap-2 text-fd-text-muted transition-colors hover:text-fd-text"
        >
          <span className="grid h-8 w-8 place-items-center rounded-full border border-fd-border bg-fd-paper transition-colors group-hover:border-fd-text">
            <ArrowLeft className="h-4 w-4" strokeWidth={2} />
          </span>
          <span className="font-geist text-[15px] font-medium tracking-[-0.005em] text-fd-text">
            {workshop.name}
          </span>
        </button>

        {!lessonsOpen ? (
          <button
            type="button"
            onClick={onToggleLessons}
            className="inline-flex items-center gap-2 rounded-[6px] border border-fd-border bg-fd-paper px-3 py-1.5 font-geist-mono text-[10.5px] font-bold uppercase tracking-[0.06em] text-fd-text-muted transition-colors hover:border-fd-text hover:text-fd-text"
            aria-label="Open lessons"
          >
            <ListVideo className="h-3.5 w-3.5" strokeWidth={2} />
            Lessons
          </button>
        ) : null}
      </header>

      {/* Circle two-column layout: video + body LEFT, lessons rail RIGHT */}
      <div
        className={cls(
          'grid gap-6 lg:gap-8',
          lessonsOpen ? 'lg:grid-cols-[minmax(0,1fr)_320px]' : 'lg:grid-cols-1',
        )}
      >
        {/* Lesson body — video first, then content below */}
        <article className="min-w-0">
          {/* The single video — only video on the page */}
          <VideoPanel
            isComplete={moduleComplete}
            isLocked={locked}
            isPlaying={isPlaying}
            module={activeModule}
            onTogglePlay={onTogglePlay}
          />

          {/* Lesson title block under the video */}
          <header className="mt-5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span className="block font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">
                {activeModule.code} · {activeModule.duration}
              </span>
              <h1 className="mt-1 font-geist text-[24px] font-medium leading-[1.15] tracking-[-0.015em] text-fd-text sm:text-[28px] [text-wrap:balance]">
                {activeModule.title}
              </h1>
            </div>
            {moduleComplete ? (
              <span
                className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-fd-ok/45 bg-fd-ok/10 px-2.5 font-geist-mono text-[10.5px] font-bold uppercase tracking-[0.05em] text-fd-ok"
                aria-label="Lesson watched"
              >
                <Check className="h-3 w-3" strokeWidth={2.5} />
                Watched
              </span>
            ) : null}
          </header>

          {/* Action strip — Mark watched primary, Ask Tom + transcript secondary */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-y border-fd-border py-3">
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onMarkWatched}
                disabled={locked || moduleComplete}
                className={cls(
                  'inline-flex h-10 items-center gap-2 rounded-[6px] border-[1.5px] px-4 font-geist-mono text-[11px] font-bold uppercase tracking-[0.05em] transition-colors',
                  moduleComplete
                    ? 'border-fd-ok/45 bg-fd-ok/10 text-fd-ok'
                    : 'border-fd-text bg-fd-accent text-[oklch(0.16_0.005_95)] hover:bg-fd-text hover:text-fd-accent',
                  locked && 'cursor-not-allowed opacity-60',
                )}
              >
                <Check className="h-3.5 w-3.5" strokeWidth={2.3} />
                {moduleComplete ? 'Watched' : 'Mark watched'}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {activeModule.transcriptHref ? (
                <a
                  href={activeModule.transcriptHref}
                  className="inline-flex h-10 items-center gap-2 rounded-[6px] border border-fd-border bg-fd-paper px-3.5 font-geist-mono text-[10.5px] font-bold uppercase tracking-[0.05em] text-fd-text-muted transition-colors hover:border-fd-text hover:text-fd-text"
                >
                  Transcript
                </a>
              ) : null}
              <a
                href="?osRoute=ask-tom"
                className="inline-flex h-10 items-center gap-2 rounded-[6px] border border-fd-border bg-fd-paper px-3.5 font-geist-mono text-[10.5px] font-bold uppercase tracking-[0.05em] text-fd-text-muted transition-colors hover:border-fd-text hover:text-fd-text"
              >
                Ask Tom
                <MessageSquare className="h-3.5 w-3.5" strokeWidth={2.1} />
              </a>
            </div>
          </div>

          {/* Lesson summary + the move */}
          <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
            <p className="text-[14.5px] leading-[1.6] text-fd-text-muted [text-wrap:pretty]">
              {activeModule.summary}
            </p>
            <div className="rounded-[10px] border border-fd-border bg-fd-paper px-4 py-3">
              <span className="block font-geist-mono text-[10px] font-bold uppercase tracking-[0.10em] text-fd-accent-deep">
                Your move
              </span>
              <p className="mt-1.5 text-[13px] leading-[1.45] text-fd-text">
                {workshop.builderMove}
              </p>
            </div>
          </div>
        </article>

        {/* Lessons rail — accordion-grouped workshops, Circle pattern (right side, closeable) */}
        {lessonsOpen ? (
          <LessonsRail
            activeModuleIndex={activeModuleIndex}
            completedModuleIds={completedModuleIds}
            onClose={onToggleLessons}
            onSelectModule={onSelectModule}
            onSelectWorkshop={onSelectWorkshop}
            selectedIndex={selectedIndex}
            selectedModuleIndex={selectedModuleIndex}
            snapshot={snapshot}
          />
        ) : null}
      </div>
    </section>
  );
}

// ── Lessons rail ──────────────────────────────────────────────────────
//
// Circle-style closeable lesson navigator. All 9 workshops are visible as
// accordion groups; the active workshop is expanded by default. Each row
// uses a calm typographic register — green checkmark for completed, lock
// for queued, plain index otherwise. No race-yellow stamps in the rail
// itself; that chrome belongs to chrome surfaces (top bar, CTAs).

function LessonsRail({
  activeModuleIndex,
  completedModuleIds,
  onClose,
  onSelectModule,
  onSelectWorkshop,
  selectedIndex,
  selectedModuleIndex,
  snapshot,
}: {
  activeModuleIndex: number;
  completedModuleIds: Set<string>;
  onClose: () => void;
  onSelectModule: (index: number) => void;
  onSelectWorkshop: (index: number) => void;
  selectedIndex: number;
  selectedModuleIndex: number;
  snapshot: BuildRoomSnapshot;
}) {
  // Track which workshops are expanded. Active workshop opens by default;
  // others collapse so the rail stays scannable for a 9-workshop course.
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(() => new Set([selectedIndex]));

  // Re-open the active workshop when the Builder switches workshops via the
  // accordion (or via a Resume jump from outside the rail).
  useEffect(() => {
    setOpenIndexes((prev) => {
      if (prev.has(selectedIndex)) return prev;
      const next = new Set(prev);
      next.add(selectedIndex);
      return next;
    });
  }, [selectedIndex]);

  const toggleWorkshop = (index: number) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <aside className="lg:sticky lg:top-6 lg:max-h-[calc(100dvh-3rem)] lg:overflow-hidden">
      <div className="flex h-full flex-col rounded-[10px] border border-fd-border bg-fd-paper">
        <header className="flex items-center justify-between border-b border-fd-border px-4 py-3">
          <h2 className="font-geist text-[14px] font-semibold tracking-[-0.005em] text-fd-text">
            Lessons
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="grid h-7 w-7 place-items-center rounded-full text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
            aria-label="Close lessons"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto py-2">
          {WORKSHOPS.map((workshop, workshopIndex) => {
            const state = workshopStateFor(snapshot.status, workshopIndex);
            const completedCount = completedCountForWorkshop(workshop, state, completedModuleIds);
            const isExpanded = openIndexes.has(workshopIndex);
            const isCurrent = workshopIndex === selectedIndex;

            return (
              <WorkshopGroup
                key={workshop.num}
                activeModuleIndex={isCurrent ? activeModuleIndex : -1}
                completedCount={completedCount}
                completedModuleIds={completedModuleIds}
                isCurrent={isCurrent}
                isExpanded={isExpanded}
                onSelectModule={(moduleIndex) => {
                  if (workshopIndex !== selectedIndex) {
                    onSelectWorkshop(workshopIndex);
                  }
                  onSelectModule(moduleIndex);
                }}
                onToggle={() => toggleWorkshop(workshopIndex)}
                selectedModuleIndex={isCurrent ? selectedModuleIndex : -1}
                state={state}
                workshop={workshop}
              />
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function WorkshopGroup({
  activeModuleIndex,
  completedCount,
  completedModuleIds,
  isCurrent,
  isExpanded,
  onSelectModule,
  onToggle,
  selectedModuleIndex,
  state,
  workshop,
}: {
  activeModuleIndex: number;
  completedCount: number;
  completedModuleIds: Set<string>;
  isCurrent: boolean;
  isExpanded: boolean;
  onSelectModule: (moduleIndex: number) => void;
  onToggle: () => void;
  selectedModuleIndex: number;
  state: WorkshopState;
  workshop: Workshop;
}) {
  const total = workshop.modules.length;
  const allComplete = state === 'done' || completedCount === total;

  return (
    <div className="px-2">
      <button
        type="button"
        onClick={onToggle}
        className={cls(
          'flex w-full items-center gap-2 rounded-[6px] px-2 py-2 text-left transition-colors',
          isCurrent ? 'text-fd-text' : 'text-fd-text-muted hover:text-fd-text',
        )}
        aria-expanded={isExpanded}
      >
        <span className="grid h-5 w-5 shrink-0 place-items-center text-fd-text-subtle">
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.2} />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-tight tracking-[-0.005em]">
          {workshop.num}. {workshop.name}
        </span>
        <span className="shrink-0 font-geist-mono text-[10px] font-semibold tabular-nums text-fd-text-subtle">
          {allComplete ? (
            <Check className="h-3.5 w-3.5 text-fd-ok" strokeWidth={2.6} />
          ) : (
            `${completedCount}/${total}`
          )}
        </span>
      </button>

      {isExpanded ? (
        <ul className="mb-2 grid gap-0.5 pb-1 pl-7 pr-1">
          {workshop.modules.map((module, moduleIndex) => {
            const moduleState = moduleStateFor(
              state,
              moduleIndex,
              activeModuleIndex,
              isModuleComplete(workshop, module, state, completedModuleIds),
            );
            return (
              <li key={module.code}>
                <ModuleRow
                  module={module}
                  state={moduleState}
                  selected={isCurrent && moduleIndex === selectedModuleIndex}
                  onSelect={() => onSelectModule(moduleIndex)}
                />
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function VideoPanel({
  isComplete,
  isLocked,
  isPlaying,
  module,
  onTogglePlay,
}: {
  isComplete: boolean;
  isLocked: boolean;
  isPlaying: boolean;
  module: VideoModule;
  onTogglePlay: () => void;
}) {
  const hasVideo = Boolean(module.videoSrc);

  return (
    <div className="overflow-hidden rounded-[10px] border-[1.5px] border-fd-text bg-fd-bg shadow-[4px_4px_0_0_var(--fd-text)]">
      <div className="relative w-full overflow-hidden aspect-video">
        {hasVideo ? (
          <video
            className="h-full w-full bg-[oklch(0.16_0.005_95)] object-cover"
            controls
            poster={module.posterSrc}
            preload="metadata"
            src={module.videoSrc}
          />
        ) : (
          <>
            <div className="absolute inset-0 opacity-[0.55]" aria-hidden>
              <div className="h-full w-full bg-[linear-gradient(to_right,var(--fd-grid)_1px,transparent_1px),linear-gradient(to_bottom,var(--fd-grid)_1px,transparent_1px)] bg-[size:30px_30px]" />
            </div>

            <div className="absolute left-[44%] top-[17%] hidden h-[42%] w-[34%] -translate-x-1/2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-paper/80 p-4 shadow-[2px_2px_0_0_var(--fd-border)] sm:block">
              <div className="h-2 w-3/4 rounded-full bg-fd-text/25" />
              <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div className="space-y-2">
                  <span className="block h-2 rounded-full bg-fd-accent" />
                  <span className="block h-2 rounded-full bg-fd-text/30" />
                  <span className="block h-2 rounded-full bg-fd-text/20" />
                </div>
                <ArrowRight className="h-4 w-4 text-fd-teal" strokeWidth={2.4} />
                <div className="space-y-2">
                  <span className="block h-5 rounded-[3px] border border-fd-text/50 bg-fd-paper" />
                  <span className="block h-5 rounded-[3px] border border-fd-text/50 bg-fd-paper" />
                </div>
              </div>
            </div>

            <Builder
              variant="reading-notebook"
              className="absolute bottom-[11%] left-[11%] w-[18%] min-w-[70px] max-w-[120px] drop-shadow-[0_18px_14px_rgba(22,18,10,0.15)]"
            />
            <span className="absolute bottom-[9%] left-[10%] h-3 w-[20%] rounded-full bg-fd-text/10 blur-sm" aria-hidden />

            <Tom
              variant="hands-on-hips"
              className="absolute w-[15%] min-w-[62px] max-w-[108px] drop-shadow-[0_18px_14px_rgba(22,18,10,0.15)]"
              style={{ bottom: '10%', right: '7%' }}
            />
            <span className="absolute bottom-[8%] right-[12%] h-3 w-[22%] rounded-full bg-fd-text/10 blur-sm" aria-hidden />

            <button
              type="button"
              onClick={onTogglePlay}
              disabled={isLocked}
              className={cls(
                'absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-fd-accent-deep bg-fd-accent text-[oklch(0.16_0.005_95)] shadow-[0_14px_30px_rgba(22,18,10,0.20)] transition-transform hover:scale-[1.03]',
                isLocked && 'cursor-not-allowed opacity-60',
              )}
              aria-label={`${isPlaying ? 'Pause' : 'Play'} ${module.title}`}
            >
              {isPlaying ? (
                <Pause className="h-7 w-7 fill-current" strokeWidth={2.25} />
              ) : (
                <Play className="ml-1 h-7 w-7 fill-current" strokeWidth={2.25} />
              )}
            </button>

            <div className="absolute inset-x-0 bottom-0 flex h-12 items-center gap-3 bg-[oklch(0.16_0.005_95)] px-4 text-[oklch(0.99_0.003_95)]">
              {isPlaying ? (
                <Pause className="h-4 w-4 fill-current" strokeWidth={2} />
              ) : (
                <Play className="h-4 w-4 fill-current" strokeWidth={2} />
              )}
              <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.04em]">
                {isComplete ? 'Watched' : isPlaying ? 'Playing' : 'Ready'} / {module.duration}
              </span>
              <span className="h-px flex-1 bg-fd-paper/30">
                <span
                  className={cls('block h-px bg-fd-accent', isComplete ? 'w-full' : isPlaying ? 'w-1/3' : 'w-0')}
                  aria-hidden
                />
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ModuleRow({
  module,
  state,
  selected,
  onSelect,
}: {
  module: VideoModule;
  state: ModuleState;
  selected: boolean;
  onSelect: () => void;
}) {
  const locked = state === 'locked';

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={locked}
      aria-current={selected ? 'true' : undefined}
      className={cls(
        'group flex w-full items-center gap-2.5 rounded-[6px] px-2 py-1.5 text-left transition-colors',
        selected ? 'bg-fd-surface-1' : 'hover:bg-fd-surface-1',
        locked && 'cursor-not-allowed opacity-65 hover:bg-transparent',
      )}
    >
      {/* Step indicator — Circle-style: green check when done, lock when queued, plain index otherwise */}
      <span
        className={cls(
          'grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-geist-mono font-semibold tabular-nums transition-colors',
          state === 'done'
            ? 'bg-fd-ok/15 text-fd-ok'
            : state === 'locked'
              ? 'text-fd-text-subtle'
              : selected
                ? 'border border-fd-text bg-fd-paper text-fd-text'
                : 'border border-fd-border bg-fd-paper text-fd-text-subtle group-hover:border-fd-text/50',
        )}
      >
        {state === 'done' ? (
          <Check className="h-3 w-3" strokeWidth={2.75} />
        ) : state === 'locked' ? (
          <LockKeyhole className="h-2.5 w-2.5" strokeWidth={2.2} />
        ) : (
          <span>{module.code.replace('.', '')}</span>
        )}
      </span>
      <span
        className={cls(
          'min-w-0 flex-1 truncate text-[12.5px] leading-[1.3] tracking-[-0.005em]',
          selected ? 'font-medium text-fd-text' : 'text-fd-text-muted',
        )}
      >
        {module.title}
      </span>
      <span className="shrink-0 font-geist-mono text-[9.5px] font-medium tabular-nums text-fd-text-subtle">
        {module.duration}
      </span>
    </button>
  );
}

function LoadWarning({ message }: { message: string }) {
  return (
    <div className="mt-5 rounded-[8px] border border-fd-coral/40 bg-fd-coral-soft px-4 py-3 text-[13px] leading-[1.5] text-fd-text">
      {message}
    </div>
  );
}

function currentWorkshopIndex(status: BuildRoomStatus): number {
  if (status === 'handed_off' || status === 'ready') return WORKSHOPS.length - 1;
  if (status === 'qa_review') return 7;
  if (status === 'building' || status === 'needs_decision' || status === 'blocked') return 1;
  return 0;
}

function workshopStateFor(status: BuildRoomStatus, index: number): WorkshopState {
  if (status === 'handed_off') return 'done';

  const current = currentWorkshopIndex(status);
  if (index < current) return 'done';
  if (index > current) return 'queued';

  if (status === 'needs_decision' || status === 'blocked' || status === 'qa_review') {
    return 'review';
  }
  return 'active';
}

function moduleStateFor(
  workshopState: WorkshopState,
  index: number,
  activeModuleIndex: number,
  complete = false,
): ModuleState {
  if (complete) return 'done';
  if (workshopState === 'done') return 'done';
  if (workshopState === 'queued') return 'locked';
  if (index < activeModuleIndex) return 'done';
  if (index === activeModuleIndex) return 'active';
  return 'queued';
}

function durationToMinutes(duration: string): number {
  const [minutes = '0', seconds = '0'] = duration.split(':');
  return Number(minutes) + (Number(seconds) > 0 ? 1 : 0);
}

function totalMinutesForWorkshop(workshop: Workshop): number {
  return workshop.modules.reduce((sum, module) => sum + durationToMinutes(module.duration), 0);
}

function moduleId(workshop: Workshop, module: VideoModule): string {
  return `${workshop.num}:${module.code}`;
}

function scopedStorageKey(baseKey: string, userId?: string | null) {
  return `${baseKey}:${userId || 'anonymous'}`;
}

function readStoredProgress(storageKey = PROGRESS_STORAGE_KEY): BuildRoomProgress {
  if (typeof window === 'undefined') return { completedModuleIds: [], currentModuleId: null };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? '{}');
    return {
      completedModuleIds: Array.isArray(parsed.completedModuleIds)
        ? parsed.completedModuleIds.filter((id: unknown): id is string => typeof id === 'string')
        : [],
      currentModuleId: typeof parsed.currentModuleId === 'string' ? parsed.currentModuleId : null,
    };
  } catch {
    return { completedModuleIds: [], currentModuleId: null };
  }
}

function writeStoredProgress(progress: BuildRoomProgress, storageKey = PROGRESS_STORAGE_KEY) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(progress));
  } catch {
    // The in-memory progress state still works if storage is unavailable.
  }
}

function findModuleLocation(id: string | null) {
  if (!id) return null;
  for (const [workshopIndex, workshop] of WORKSHOPS.entries()) {
    const moduleIndex = workshop.modules.findIndex((module) => moduleId(workshop, module) === id);
    if (moduleIndex >= 0) return { workshopIndex, moduleIndex };
  }
  return null;
}

// Lab home reads the Builder's last Build Room location to specialise the
// "next move" CTA without coupling to internal types or the storage shape.
export interface BuildRoomCurrentLocation {
  workshopNum: string;
  workshopName: string;
  moduleCode: string;
  moduleTitle: string;
  moduleSummary: string;
  moduleDuration: string;
}

export function readBuildRoomCurrentLocation(): BuildRoomCurrentLocation | null {
  if (typeof window === 'undefined') return null;
  const progress = readStoredProgress();
  const location = findModuleLocation(progress.currentModuleId);
  if (!location) return null;
  const workshop = WORKSHOPS[location.workshopIndex];
  const module = workshop?.modules[location.moduleIndex];
  if (!workshop || !module) return null;
  return {
    workshopNum: workshop.num,
    workshopName: workshop.name,
    moduleCode: module.code,
    moduleTitle: module.title,
    moduleSummary: module.summary,
    moduleDuration: module.duration,
  };
}

function isModuleComplete(
  workshop: Workshop,
  module: VideoModule,
  workshopState: WorkshopState,
  completedModuleIds: Set<string>,
): boolean {
  return workshopState === 'done' || completedModuleIds.has(moduleId(workshop, module));
}

function completedCountForWorkshop(
  workshop: Workshop,
  workshopState: WorkshopState,
  completedModuleIds: Set<string>,
): number {
  if (workshopState === 'done') return workshop.modules.length;
  return workshop.modules.filter((module) => completedModuleIds.has(moduleId(workshop, module))).length;
}

function nextModuleIndexFor(
  workshop: Workshop,
  workshopState: WorkshopState,
  completedModuleIds: Set<string>,
): number {
  if (workshopState === 'done') return Math.max(0, workshop.modules.length - 1);
  if (workshopState === 'queued') return 0;
  const next = workshop.modules.findIndex((module) => !completedModuleIds.has(moduleId(workshop, module)));
  return next >= 0 ? next : Math.max(0, workshop.modules.length - 1);
}

function findNextOpenModuleIndex(
  workshop: Workshop,
  currentIndex: number,
  completedModuleIds: Set<string>,
): number {
  const next = workshop.modules.findIndex((module, index) =>
    index > currentIndex && !completedModuleIds.has(moduleId(workshop, module)),
  );
  if (next >= 0) return next;
  const firstOpen = workshop.modules.findIndex((module) => !completedModuleIds.has(moduleId(workshop, module)));
  return firstOpen >= 0 ? firstOpen : currentIndex;
}

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}


function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  const preview = new URLSearchParams(window.location.search).get('preview');
  return preview === 'member' || preview === 'admin';
}
